import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "./useAuth";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
	const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
	const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
	const rawData = atob(base64);
	const outputArray = new Uint8Array(rawData.length);
	for (let i = 0; i < rawData.length; ++i) {
		outputArray[i] = rawData.charCodeAt(i);
	}
	return outputArray;
}

export function usePushSubscription() {
	const { getToken, isSignedIn } = useAuth();
	const getTokenRef = useRef(getToken);
	useEffect(() => {
		getTokenRef.current = getToken;
	}, [getToken]);

	const [subscription, setSubscription] = useState<PushSubscription | null>(null);
	const [permission, setPermission] = useState<NotificationPermission>("default");
	const supported = typeof navigator !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;

	/* eslint-disable react-hooks/set-state-in-effect */
	useEffect(() => {
		if (supported) {
			setPermission(Notification.permission);
		}
	}, [supported]);
	/* eslint-enable react-hooks/set-state-in-effect */

	const subscribe = useCallback(async () => {
		if (!isSignedIn || !supported) return false;
		if (permission === "denied") return false;

		try {
			const reg = await navigator.serviceWorker.ready;

			const token = await getTokenRef.current();
			if (!token) return false;

			const res = await api.vapidPublicKey(token);
			if (!res.publicKey) return false;

			const existing = await reg.pushManager.getSubscription();
			if (existing) {
				setSubscription(existing);
				return true;
			}

			const sub = await reg.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: urlBase64ToUint8Array(res.publicKey).buffer as ArrayBuffer,
			});

			const keys = sub.toJSON().keys;
			if (!keys?.p256dh || !keys?.auth) return false;

			await api.pushSubscribe(token, {
				endpoint: sub.endpoint,
				p256dh: keys.p256dh,
				auth: keys.auth,
			});

			setSubscription(sub);
			setPermission("granted");
			return true;
		} catch (err) {
			console.error("Push subscribe failed:", err);
			return false;
		}
	}, [isSignedIn, supported, permission]);

	const unsubscribe = useCallback(async () => {
		if (!subscription) return;
		try {
			await subscription.unsubscribe();
		} catch {
			// ignore
		}
		try {
			const token = await getTokenRef.current();
			if (token) {
				await api.pushUnsubscribe(token, subscription.endpoint);
			}
		} catch {
			// ignore
		}
		setSubscription(null);
	}, [subscription]);

	const requestPermission = useCallback(async () => {
		if (!supported) return "denied" as NotificationPermission;
		const result = await Notification.requestPermission();
		setPermission(result);
		return result;
	}, [supported]);

	useEffect(() => {
		if (!isSignedIn || !supported || permission !== "granted") return;

		navigator.serviceWorker.ready.then((reg) => {
			reg.pushManager.getSubscription().then((sub) => {
				if (sub) setSubscription(sub);
			});
		});
	}, [isSignedIn, supported, permission]);

	return {
		supported,
		permission,
		subscription,
		subscribe,
		unsubscribe,
		requestPermission,
	};
}
